import type { Patient, Sex } from "@jasper/client-models";
import { useTranslation } from "react-i18next";
import {
  Group,
  Stack,
  TextInput,
  Card,
  RadioGroup,
  Radio,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";

import type { FieldValueProps } from "../../types";

type Props = FieldValueProps<Patient>;

const SEX_VALUES: Sex[] = ["MALE", "FEMALE"];

export const PatientForm = ({ value, errors, onChange, onBlur }: Props) => {
  const { t } = useTranslation("forms");

  const getError = (error: string | undefined): string | undefined =>
    error ? t("common.toProvide") : undefined;

  const sexChoices = SEX_VALUES.map(s => ({
    value: s,
    label: t(`forms.patient.sex.${s}`),
  }));

  return (
    <Stack gap="md">
      <Group gap="md">
        <Card flex={1}>
          <TextInput
            label={t("forms.patient.labels.firstName")}
            required
            value={value?.firstName ?? ""}
            onChange={e =>
              onChange({ ...value, firstName: e.currentTarget.value })
            }
            onBlur={() => onBlur?.()}
            error={getError(errors?.firstName)}
          />
        </Card>
        <Card flex={1}>
          <TextInput
            label={t("forms.patient.labels.lastName")}
            required
            value={value?.lastName ?? ""}
            onChange={e =>
              onChange({ ...value, lastName: e.currentTarget.value })
            }
            onBlur={() => onBlur?.()}
            error={getError(errors?.lastName)}
          />
        </Card>
      </Group>
      <Group gap="md">
        <Card flex={1}>
          <DateInput
            label={t("forms.patient.labels.birthDate")}
            value={(value?.birthDate as Date | undefined) ?? null}
            onChange={v =>
              onChange({ ...value, birthDate: v ? new Date(v) : undefined })
            }
            onBlur={() => onBlur?.()}
            valueFormat={t("common.dateFormat")}
            clearable
          />
        </Card>
        <Card flex={1} style={{ height: "stretch" }}>
          <RadioGroup
            name="option"
            defaultValue={value?.sex ?? null}
            onChange={v => onChange({ ...value, sex: (v as Sex) ?? undefined })}
            label={t("forms.patient.labels.sex")}
          >
            <Group>
              {sexChoices.map(choice => (
                <Radio
                  value={choice.value}
                  key={choice.value}
                  label={choice.label}
                />
              ))}
            </Group>
          </RadioGroup>
        </Card>
      </Group>
    </Stack>
  );
};
